import { useEffect, useRef, useState } from "react";

/**
 * Animates a number from 0 to `targetValue` over `duration` ms.
 * Uses requestAnimationFrame for smooth counting with easing.
 */
export function useCountUp(targetValue: number, duration = 500): number {
  const [value, setValue] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const targetRef = useRef(targetValue);

  useEffect(() => {
    targetRef.current = targetValue;
    startTimeRef.current = null;
    setValue(0);
  }, [targetValue]);

  useEffect(() => {
    let animationFrameId: number;

    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);

      setValue(Math.round(easedProgress * targetRef.current));

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrameId);
  }, [duration, targetValue]);

  return value;
}
